import {
    Component,
    AfterViewInit,
    ChangeDetectorRef,
    HostListener,
    ViewChild,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { confirm } from 'devextreme/ui/dialog';
import moment from 'moment';
import {
    debounce as _debounce,
    DebouncedFunc,
    each as _each,
    toNumber as _toNumber,
    includes as _includes,
    toString as _toString,
    reduce as _reduce,
    isFinite as _isFinite,
    get as _get,
    isEmpty as _isEmpty,
    isString as _isString,
    isNumber as _isNumber,
    trim as _trim,
} from 'lodash';
import {
    ThAddressSuggestorService,
    PrintSheetService,
    NotificationService,
    UtilService,
    MessageType,
    RenderPersistReportService,
} from 'src/app/shared/services';
import { MessageService } from '../../services/message.service';
import {
    getInitialInvoiceInput,
    ProductInfo,
    TaxInvoiceInput,
} from '../../models/invoice-input';
import { branchList } from 'src/app/shared/config/branch-list';
import {
    toSellerPrintBlock,
    vatRegisteredEntityById,
} from 'src/app/shared/config/entity-config';
import { sellerFor } from 'src/app/shared/config/store-config';
import { splitVatInclusive, vatRateAt } from 'src/app/shared/config/vat';
import {
    refuseTaxInvoice,
    validateFullTaxInvoice,
} from 'src/app/shared/config/tax-invoice-guards';
import {
    buildInvoiceSaveRequest,
    buildTaxInvoicePrintSheetRequest,
} from '../../models/build-print-param';
import { TaxInvoicePrintSheetParameter } from 'src/app/shared/services/print-sheet/interfaces/tax-invoice-param';
import { RenderRequest } from 'src/app/shared/services/print-sheet/interfaces/report-api-request';
import { environment } from 'src/environments/environment';
import { DxValidationGroupComponent } from 'devextreme-angular';
import { OutputTaxService } from 'src/app/shared/services/output-tax/tax-output.service';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ClickEvent } from 'devextreme/ui/button';
import { DxLoadPanelComponent } from 'devextreme-angular/ui/load-panel';
import { DxValidationGroupComponent as DxValidationGroupComponent_1 } from 'devextreme-angular/ui/validation-group';
import { DxBoxComponent } from 'devextreme-angular/ui/box';
import {
    DxiItemComponent,
    DxiValidationRuleComponent,
    DxoEditingComponent,
    DxoRowDraggingComponent,
    DxiColumnComponent,
} from 'devextreme-angular/ui/nested';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxNumberBoxComponent } from 'devextreme-angular/ui/number-box';
import { DxDateBoxComponent } from 'devextreme-angular/ui/date-box';
import { DxValidatorComponent } from 'devextreme-angular/ui/validator';
import { DxSwitchComponent } from 'devextreme-angular/ui/switch';
import { NgClass } from '@angular/common';
import { DxSelectBoxComponent } from 'devextreme-angular/ui/select-box';
import { DxAutocompleteComponent } from 'devextreme-angular/ui/autocomplete';
import { DxTextAreaComponent } from 'devextreme-angular/ui/text-area';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { FormatNumberPipe } from '../../../../shared/pipes/format-number.pipe';

@Component({
    selector: 'app-create-tax-invoice',
    templateUrl: './create-tax-invoice.component.html',
    styleUrls: ['./create-tax-invoice.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxLoadPanelComponent,
        DxValidationGroupComponent_1,
        DxBoxComponent,
        DxiItemComponent,
        DxButtonComponent,
        DxTextBoxComponent,
        DxNumberBoxComponent,
        DxDateBoxComponent,
        DxValidatorComponent,
        DxiValidationRuleComponent,
        DxSwitchComponent,
        NgClass,
        DxSelectBoxComponent,
        DxAutocompleteComponent,
        DxTextAreaComponent,
        DxDataGridComponent,
        DxoEditingComponent,
        DxoRowDraggingComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        FormatNumberPipe,
    ],
})
export class CreateTaxInvoiceComponent implements AfterViewInit {
    private cdref = inject(ChangeDetectorRef);
    private thAddressSuggestor = inject(ThAddressSuggestorService);
    private printSheetService = inject(PrintSheetService);
    private renderPersistService = inject(RenderPersistReportService);
    private messageService = inject(MessageService);
    private utilService = inject(UtilService);
    private notificationService = inject(NotificationService);
    private outputTaxService = inject(OutputTaxService);

    @ViewChild('validationGroup', { static: true })
    validationGroup!: DxValidationGroupComponent;

    // The manual tax-invoice form has no originating order, so the seller is
    // the offline rule: genlogic-co, always (spec §4.4.1). The narrow to the
    // registered branch is safe by the same rule, and hands us the archive
    // folders.
    private readonly sellerEntity = vatRegisteredEntityById(
        sellerFor({ offline: true }).id,
    );
    private readonly seller = toSellerPrintBlock(this.sellerEntity);
    // Un-narrowed flag for the template: when the seller entity is not VAT
    // registered, the tax-invoice document type is forced off and
    // non-editable (spec W3) — not merely defaulted off. Constant true while
    // the offline rule resolves to genlogic-co; the binding enforces the rule,
    // not today's value.
    readonly sellerVatRegistered = sellerFor({ offline: true }).vatRegistered;

    branchList = branchList;
    postCodeSuggestions: any[] = [];
    provinceSuggestions: any[] = [];
    districtSuggestions: any[] = [];
    subDistrictSuggestions: any[] = [];

    shareData: {
        fileName: string;
        sharedUrl: string;
    } | null = null;
    isExporting = false;
    requestData!: string;
    messageSourceWindow: Window | null = null;

    inputValues: TaxInvoiceInput = getInitialInvoiceInput();
    onInputsUpdated: DebouncedFunc<() => void>;

    private previousSuggestionSelection = '';

    constructor() {
        this.onInputsUpdated = _debounce(this.internalOnInputsUpdated, 500);
        this.onReorder = this.onReorder.bind(this);
    }

    @HostListener('window:message', ['$event'])
    onMessage(event: any): void {
        const responseStatus: string = _get(event, 'data.status');
        if (responseStatus === 'ready') {
            this.messageSourceWindow = event.source;
        } else if (responseStatus === 'data' && event.data.data) {
            this.messageService.fillInputFromMessage(
                event.data.data,
                this.inputValues,
            );
            this.cdref.detectChanges();
            this.notificationService.pushMessage({
                message: 'Lnwshop data is loaded',
                messageType: MessageType.success,
            });
        } else if (responseStatus === 'error') {
            this.notificationService.pushMessage({
                message: `Lnwshop Data loading failed with responsed error: ${event.data.message}`,
                messageType: MessageType.error,
            });
        }
    }

    requestMessage(): void {
        if (this.messageSourceWindow) {
            this.messageSourceWindow.postMessage(
                {
                    status: 'request',
                },
                '*',
            );
        } else {
            console.error('No connection');
        }
    }

    ngAfterViewInit(): void {
        this.postCodeSuggestions = this.thAddressSuggestor.getAllZipCodes();
        this.provinceSuggestions = this.thAddressSuggestor.getAllProvinces();
        this.fillInitialValues(this.inputValues);
        // DEBUG Message
        // this.messageService.fillInputFromMessage(a, this.inputValues);
        // this.cdref.detectChanges();
    }

    clearForm(): void {
        confirm('Filled data will be reset.', 'Confirm Clear Data ?').then(
            (result: boolean) => {
                if (result) {
                    this.inputValues = getInitialInvoiceInput();
                    this.fillInitialValues(this.inputValues);
                    this.validationGroup.instance?.reset();
                    this.notificationService.pushMessage({
                        message: 'Form is cleared.',
                        messageType: MessageType.info,
                    });
                }
            },
        );
    }

    showPreviewPdf(isCopy = false): void {
        if (!this.isExporting) {
            this.constructFullAddress();
            this.calculatePrices();
            this.cdref.detectChanges();
            const request: TaxInvoicePrintSheetParameter =
                buildTaxInvoicePrintSheetRequest(
                    this.inputValues,
                    this.seller,
                    'INV0000000000',
                    isCopy,
                );
            let tabTitle = 'ใบกำกับภาษี';
            if (request.orderInfo.invNum) {
                tabTitle = request.orderInfo.invNum;
            }
            this.isExporting = true;
            this.printSheetService
                .printTaxInvoice([request], tabTitle)
                .subscribe(
                    () => {
                        this.isExporting = false;
                        this.notificationService.pushMessage({
                            message: 'Tax invoice is successfully exported.',
                            messageType: MessageType.success,
                        });
                    },
                    (error) => {
                        this.isExporting = false;
                        console.error(error);
                        this.notificationService.pushMessage({
                            message: this.utilService.errorToString(error),
                            messageType: MessageType.error,
                        });
                    },
                );
        }
    }

    saveAndShare(e: ClickEvent): void {
        if (!this.isExporting) {
            this.constructFullAddress();
            this.calculatePrices();
            this.cdref.detectChanges();
            const result = e.validationGroup.validate();
            if (_isEmpty(this.inputValues.productItems)) {
                this.notificationService.pushMessage({
                    message: 'ไม่มีรายการสินค้า',
                    messageType: MessageType.error,
                });
                return;
            } else if (!this.validateProducts()) {
                this.notificationService.pushMessage({
                    message: 'ข้อมูลรายการสินค้าไม่ครบถ้วน',
                    messageType: MessageType.error,
                });
                return;
            } else if (result.isValid) {
                const saveRequest = buildInvoiceSaveRequest(
                    this.inputValues,
                    this.sellerEntity,
                );

                // W3 hard guards — refusals, not warnings. The manual form is
                // storeless, so this is G1/G2/G3 territory plus G4's offline
                // rule; a refusal means no save, no number, no document.
                const invoiceMoment = moment(this.inputValues.invoiceInfo.date);
                const refusal = refuseTaxInvoice({
                    entityId: this.sellerEntity.id,
                    storeId: undefined,
                    sellerTaxId: saveRequest['sellerInfo.taxId'],
                    invoiceDateIso: invoiceMoment.isValid()
                        ? invoiceMoment.format('YYYY-MM-DD')
                        : '',
                    isTaxInvoice: this.inputValues.invoiceInfo.isTaxInvoice,
                });
                if (refusal) {
                    this.notificationService.pushMessage({
                        message: refusal,
                        messageType: MessageType.error,
                    });
                    return;
                }

                // W13 content validation — WARN severity for now (user
                // decision): surface what a full ม.86/4 document is missing,
                // but let the operator proceed.
                if (this.inputValues.invoiceInfo.isTaxInvoice) {
                    const warnings = validateFullTaxInvoice({
                        name: saveRequest['customerInfo.name'],
                        address: saveRequest['customerInfo.address'],
                        taxId: saveRequest['customerInfo.taxId'],
                        branch: saveRequest['customerInfo.branch'],
                    });
                    if (warnings.length > 0) {
                        this.notificationService.pushMessage({
                            message: warnings.join(' / '),
                            messageType: MessageType.warning,
                        });
                    }
                }

                this.shareData = null;
                this.isExporting = true;

                let usingInvNum: string;
                this.outputTaxService
                    .saveTaxInvoice(saveRequest)
                    .pipe(
                        switchMap((response) => {
                            if (response.status === 200 && response.invNum) {
                                usingInvNum = response.invNum;
                                return this.createTaxInvoiceAndGetSharedUrl(
                                    usingInvNum,
                                    this.inputValues.invoiceInfo.includeCopy,
                                );
                            }
                            return throwError('Save invoice failed.');
                        }),
                        map(([originalSharedUrlResponse]) => {
                            this.shareData = {
                                fileName: originalSharedUrlResponse.name,
                                sharedUrl:
                                    originalSharedUrlResponse.webViewLink,
                            };
                        }),
                        switchMap(() => {
                            return this.outputTaxService.saveTaxInvoiceSharedUrl(
                                usingInvNum,
                                this.shareData!.sharedUrl,
                            );
                        }),
                        switchMap((savedSharedUrlResult) => {
                            if (
                                _get(savedSharedUrlResult, 'modifiedCount', 0) >
                                0
                            ) {
                                return of(true);
                            }
                            return throwError('Shared url cannot be save.');
                        }),
                    )
                    .subscribe(
                        () => {
                            this.isExporting = false;
                            this.openSharedUrl();
                            this.notificationService.pushMessage({
                                message: `${
                                    this.shareData!.fileName
                                } is saved.`,
                                messageType: MessageType.success,
                            });
                        },
                        (error) => {
                            this.isExporting = false;
                            console.error(error);
                            this.notificationService.pushMessage({
                                message: this.utilService.errorToString(error),
                                messageType: MessageType.error,
                            });
                        },
                    );
            }
        }
    }

    private validateProducts() {
        const strRequiredFields = ['sku', 'description'];
        const numRequiredFields = [
            'pid',
            'itemNo',
            'unitPrice',
            'quantity',
            'amount',
        ];

        let isStrValid = true;
        _each(this.inputValues.productItems, (product: ProductInfo) => {
            _each(strRequiredFields, (field: string) => {
                const fieldValue: any = _isString((product as any)[field])
                    ? _trim((product as any)[field])
                    : (product as any)[field];
                isStrValid = _isString(fieldValue) && !_isEmpty(fieldValue);
                return isStrValid;
            });
        });

        let isNumValid = true;
        _each(this.inputValues.productItems, (product: ProductInfo) => {
            _each(numRequiredFields, (field: string) => {
                isNumValid =
                    _isNumber((product as any)[field]) &&
                    _isFinite((product as any)[field]);
                return isNumValid;
            });
        });

        return isStrValid && isNumValid;
    }

    private createTaxInvoiceAndGetSharedUrl(
        invNum: string,
        includeCopy: boolean,
    ): Observable<any> {
        const originalRequest: TaxInvoicePrintSheetParameter =
            buildTaxInvoicePrintSheetRequest(
                this.inputValues,
                this.seller,
                invNum,
            );
        const payload: RenderRequest<TaxInvoicePrintSheetParameter> = {
            data: { pages: [originalRequest] },
            template: {
                name: environment.taxInvoiceReportTemplateName,
            },
        };
        const obList: Observable<any>[] = [
            this.renderPersistService.renderAndGetSharedUrl<TaxInvoicePrintSheetParameter>(
                {
                    renderRequest: payload,
                    reportStoreRequest: {
                        fileName: invNum ? `${invNum}.pdf` : 'untitled.pdf',
                        parents: [this.sellerEntity.invoiceDriveFolderId],
                    },
                },
            ),
        ];

        if (includeCopy) {
            const copyRequest: TaxInvoicePrintSheetParameter =
                buildTaxInvoicePrintSheetRequest(
                    this.inputValues,
                    this.seller,
                    invNum,
                    true,
                );
            const copyPayload: RenderRequest<TaxInvoicePrintSheetParameter> = {
                data: { pages: [copyRequest] },
                template: {
                    name: environment.taxInvoiceReportTemplateName,
                },
            };
            obList.push(
                this.renderPersistService.renderAndGetSharedUrl<TaxInvoicePrintSheetParameter>(
                    {
                        renderRequest: copyPayload,
                        reportStoreRequest: {
                            fileName: invNum
                                ? `${invNum} (สำเนา).pdf`
                                : 'untitled (สำเนา).pdf',
                            parents: [
                                this.sellerEntity.invoiceCopyDriveFolderId,
                            ],
                        },
                    },
                ),
            );
        }
        return forkJoin(obList);
    }

    openSharedUrl(): void {
        const sharedUrl = _get(this.shareData, 'sharedUrl');
        if (sharedUrl) {
            window.open(sharedUrl, '_blank');
        }
    }

    copySharedUrlToClibboard(): void {
        const sharedUrl = _get(this.shareData, 'sharedUrl');
        if (sharedUrl) {
            navigator.clipboard.writeText(sharedUrl).then(
                () => {
                    this.notificationService.pushMessage({
                        message: 'URL copied to clipboard.',
                        messageType: MessageType.success,
                        displayTime: 100,
                    });
                },
                (e) => {
                    console.error(e);
                },
            );
        }
    }

    calculateProductsAmount(rowData: ProductInfo): number {
        const unitPrice = _get(rowData, 'unitPrice');
        const quantity = _get(rowData, 'quantity');
        if (_isFinite(unitPrice) && _isFinite(quantity)) {
            return rowData.unitPrice * rowData.quantity;
        }
        return 0;
    }

    onSaved(): void {
        this.generateProductOrderNumber();
        this.onInputsUpdated();
    }

    onReorder(e: any): void {
        const visibleRows = e.component.getVisibleRows();
        const toIndex = this.inputValues.productItems.indexOf(
            visibleRows[e.toIndex].data,
        );
        const fromIndex = this.inputValues.productItems.indexOf(e.itemData);
        this.inputValues.productItems.splice(fromIndex, 1);
        this.inputValues.productItems.splice(toIndex, 0, e.itemData);
        this.generateProductOrderNumber();
    }

    private fillInitialValues(input: TaxInvoiceInput): void {
        input.invoiceInfo.date = moment().toDate();
        if (!this.sellerVatRegistered) {
            // Forced off, and the switch is disabled in the template — a
            // non-registered seller must never issue a tax invoice (G3/G1).
            input.invoiceInfo.isTaxInvoice = false;
        }
        this.shareData = null;
        this.cdref.detectChanges();
    }

    private internalOnInputsUpdated(): void {
        this.suggestAddressIfNeeded();
        this.constructFullAddress();
        this.calculatePrices();
        this.cdref.detectChanges();
    }

    private calculatePrices(): void {
        _each(this.inputValues.productItems, (prduct: ProductInfo) => {
            if (_isFinite(prduct.unitPrice) && _isFinite(prduct.quantity)) {
                prduct.amount = prduct.unitPrice * prduct.quantity;
            } else {
                prduct.amount = 0;
            }
        });
        const productPrice: number = _reduce(
            this.inputValues.productItems,
            (total: number, item) => {
                const amountNum = _toNumber(item.amount);
                if (_isFinite(amountNum)) {
                    return total + item.amount;
                }
                return total;
            },
            0,
        );
        this.inputValues.invoiceInfo.totalProductPrice = productPrice;

        const shippingCost =
            _toNumber(this.inputValues.invoiceInfo.shippingCost) || 0;

        this.inputValues.invoiceInfo.tableTotal = productPrice + shippingCost;
        const discount = _toNumber(this.inputValues.invoiceInfo.discount) || 0;

        // The rate is the one in force on the invoice date — the tax point —
        // so regenerating an old document can never silently re-rate it.
        const invoiceMoment = moment(this.inputValues.invoiceInfo.date);
        const invoiceDateIso = invoiceMoment.isValid()
            ? invoiceMoment.format('YYYY-MM-DD')
            : undefined;

        if (this.inputValues.invoiceInfo.priceIncludesVat) {
            // Marketplace reality: the entered prices already contain VAT.
            // The gross is what the buyer paid; base and VAT are back-solved
            // from it exactly (shipping is inside tableTotal, so it is taxed
            // — §4.7).
            const gross = this.inputValues.invoiceInfo.tableTotal - discount;
            const split = splitVatInclusive(gross, invoiceDateIso);
            this.inputValues.invoiceInfo.amtBeforeVat = split.base;
            this.inputValues.invoiceInfo.vatAmount = split.output;
            this.inputValues.invoiceInfo.netPrice = gross;
        } else {
            // Add-on-top: right for quotations and other pre-VAT pricing.
            const amtBeforeVat: number =
                this.inputValues.invoiceInfo.tableTotal - discount;

            this.inputValues.invoiceInfo.amtBeforeVat = amtBeforeVat;

            let vatAmount =
                _toNumber(amtBeforeVat) * vatRateAt(invoiceDateIso) || 0;
            vatAmount = Math.round((vatAmount + Number.EPSILON) * 100) / 100;
            this.inputValues.invoiceInfo.vatAmount = vatAmount;

            const netPrice: number = amtBeforeVat + vatAmount;
            this.inputValues.invoiceInfo.netPrice = netPrice;
        }
    }

    private suggestAddressIfNeeded(): void {
        const selectedPostCode =
            _toString(this.inputValues.customerInfo.postCode) || '';
        const selectedProvince = this.inputValues.customerInfo.province || '';
        const selectedDistrict = this.inputValues.customerInfo.district || '';

        const currentSuggestionSelection =
            selectedPostCode + selectedProvince + selectedDistrict;

        if (this.previousSuggestionSelection !== currentSuggestionSelection) {
            this.previousSuggestionSelection = currentSuggestionSelection;
            if (selectedPostCode.length === 5) {
                const selectedPostCodeNum = _toNumber(selectedPostCode);
                const province: string =
                    this.thAddressSuggestor.getProvinceByPostCode(
                        selectedPostCodeNum,
                    );
                this.provinceSuggestions = [province];

                if (selectedProvince) {
                    const districts: string[] =
                        this.thAddressSuggestor.getDistrictsByPostCode(
                            selectedPostCodeNum,
                        );

                    this.districtSuggestions = districts;
                } else {
                    this.districtSuggestions = [];
                }

                if (selectedDistrict) {
                    const subDistricts: string[] =
                        this.thAddressSuggestor.getSubDistrictsByDistrict(
                            selectedPostCodeNum,
                            selectedDistrict,
                        );

                    this.subDistrictSuggestions = subDistricts;
                } else {
                    this.subDistrictSuggestions = [];
                }
            } else {
                this.provinceSuggestions =
                    this.thAddressSuggestor.getAllProvinces();
            }
        }
    }

    private constructFullAddress(): void {
        const address1 = this.inputValues.customerInfo.address1?.trim();
        const subDistrict = this.inputValues.customerInfo.subDistrict?.trim();
        const district = this.inputValues.customerInfo.district?.trim();
        const province = this.inputValues.customerInfo.province?.trim();
        const postCode = _toString(
            this.inputValues.customerInfo.postCode,
        ).trim();
        let result = '';
        if (address1) {
            result += address1;
        }
        const isBangkok = _includes(province, 'กรุงเทพ');
        if (subDistrict) {
            result += isBangkok ? ' แขวง' : ' ตำบล';
            result += subDistrict;
        }
        if (district) {
            result += isBangkok ? ' เขต' : ' อำเภอ';
            result += district;
        }
        if (province) {
            result += ` จังหวัด${province}`;
        }
        if (postCode) {
            result += ` ${postCode}`;
        }
        result = result.trim();
        this.inputValues.customerInfo.fullAddress = result;
    }

    private generateProductOrderNumber(): void {
        _each(this.inputValues.productItems, (item, index) => {
            item.itemNo = index + 1;
        });
    }
}
